import { useCallback, useEffect, useRef, useState } from 'react'

import { logEvent } from '../services/datadog'
import { redirectToUrl } from '../utils/location'
import { clearNativeHandoffAttempt, markNativeHandoffAttempt, readNativeHandoffAttempt } from '../utils/nativeHandoff'

export type NativeAppHandoffState = 'idle' | 'redirecting' | 'blocked'

// Chrome Custom Tabs / ASWebAuthenticationSession block an external-scheme navigation
// that isn't backed by user activation, landing on about:blank instead of leaving the
// page. If the document is still visible this long after firing the hop, it was blocked
// rather than merely slow (opening the native app is near-instant once allowed).
export const NATIVE_HANDOFF_TIMEOUT_MS = 3000

export type UseNativeAppHandoff = {
  state: NativeAppHandoffState
  start: (url: string) => void
  retry: () => void
  /** Clears this attempt and returns to 'idle' — the escape hatch off the blocked
   * screen for a brand-new sign-in that only looks blocked because of a leftover
   * marker (previous attempt never cleared: crash, OS-killed tab, shared device). */
  reset: () => void
}

/**
 * Detects a blocked hand-off to the native Swile app on `/signin/ios` and
 * `/signin/android`, so the sign-in page can show a warning instead of a blank tab.
 *
 * No-ops entirely when `enabled` is false (web/partner routes still call this hook —
 * Rules of Hooks — but must not touch storage/timers/listeners for a hop they never
 * fire).
 *
 * Two independent signals mark the hop as blocked, once the initial
 * `NATIVE_HANDOFF_TIMEOUT_MS` grace period has elapsed:
 * 1. Timeout — the document is still `visible` when the grace period elapses.
 * 2. Marker still present when the tab becomes visible again after having been
 *    hidden (covers the timeout elapsing while hidden, and a bfcache `pageshow`).
 *
 * The marker is cleared only by an explicit `reset()` (start-over) or by its own TTL
 * (see `readNativeHandoffAttempt` in `nativeHandoff.ts`) — never on `pagehide`. A
 * blocked external-scheme navigation can itself commit `about:blank`, unloading this
 * document and firing `pagehide` indistinguishably from a genuine success; clearing the
 * marker there would destroy the only evidence of the block before a remount (the user
 * returning to this tab) ever gets to see it.
 */
export const useNativeAppHandoff = (enabled: boolean): UseNativeAppHandoff => {
  const [state, setState] = useState<NativeAppHandoffState>(() => {
    if (!enabled) return 'idle'
    return readNativeHandoffAttempt() ? 'blocked' : 'idle'
  })
  const initialMarker = useRef(enabled ? readNativeHandoffAttempt() : null).current
  const urlRef = useRef<string | null>(initialMarker)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Grace period elapsed for the *current* attempt: gates markBlocked so a plain
  // tab-switch-and-back well within the grace period can't short-circuit it early.
  const timeoutElapsedRef = useRef(Boolean(initialMarker))
  // Guards against emitting the same "found blocked on mount/re-check" log twice.
  const hasLoggedBlockedRef = useRef(Boolean(initialMarker))

  const markBlocked = useCallback((reason: string): void => {
    if (!hasLoggedBlockedRef.current) {
      hasLoggedBlockedRef.current = true
      logEvent('native_handoff_blocked', undefined, { reason }, undefined, 'warn')
    }
    setState('blocked')
  }, [])

  const clearTimer = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    if (initialMarker) {
      logEvent('native_handoff_blocked', undefined, { reason: 'returned_to_page' }, undefined, 'warn')
    }

    // Only evaluates once this attempt's own grace period has elapsed, so an
    // ordinary tab-switch-and-back inside the timeout window is never itself read
    // as a signal (positive or negative) — only the timeout callback and this
    // re-check (once armed) decide the outcome.
    const evaluateIfStillBlocked = (): void => {
      if (timeoutElapsedRef.current && document.visibilityState === 'visible' && readNativeHandoffAttempt()) {
        markBlocked('still_visible')
      }
    }

    window.addEventListener('pageshow', evaluateIfStillBlocked)
    document.addEventListener('visibilitychange', evaluateIfStillBlocked)

    return () => {
      window.removeEventListener('pageshow', evaluateIfStillBlocked)
      document.removeEventListener('visibilitychange', evaluateIfStillBlocked)
      clearTimer()
    }
    // enabled/initialMarker are fixed for this component's lifetime (derived once from
    // the platform prop and the mount-time storage read) — listed for lint completeness.
  }, [enabled, initialMarker, markBlocked, clearTimer])

  // Arms (or re-arms) the grace-period timer for the attempt about to fire. Shared by
  // `start()` and `retry()` — a retry MUST reset `timeoutElapsedRef` and get its own
  // fresh timer, not inherit the already-elapsed one from the attempt it's retrying:
  // otherwise the very next visibilitychange (even a sub-second flicker during the
  // native hand-off itself) would call markBlocked with zero grace period.
  const armGracePeriod = useCallback((): void => {
    timeoutElapsedRef.current = false
    clearTimer()
    timeoutRef.current = setTimeout(() => {
      timeoutElapsedRef.current = true
      if (document.visibilityState === 'visible') {
        markBlocked('still_visible')
      }
      // Else: still hidden when the grace period elapsed — the verdict is deferred
      // to the next visibilitychange/pageshow once the tab is visible again.
    }, NATIVE_HANDOFF_TIMEOUT_MS)
  }, [markBlocked, clearTimer])

  const start = useCallback(
    (url: string): void => {
      if (!enabled) return
      if (readNativeHandoffAttempt()) {
        // A previous attempt on this tab never cleared (blocked, or a reload raced this
        // call) — the mount-time check already surfaces the warning; don't fire a second
        // blind redirect on top of it.
        return
      }
      urlRef.current = url
      setState('redirecting')
      markNativeHandoffAttempt(url)
      logEvent('native_handoff_started')
      redirectToUrl(url)
      armGracePeriod()
    },
    [enabled, armGracePeriod],
  )

  // The stored URL is a stateless, replayable `/oauth/authorize?...&current-account-uuid=<JWT>`
  // value (10-minute server-side TTL, kept above the marker's own 5-minute TTL) — not an
  // already-issued OAuth code. Replaying it is safe, so retry navigates synchronously,
  // staying in the button click's activation task instead of awaiting a re-mint first.
  const retry = useCallback((): void => {
    if (!enabled || !urlRef.current) return

    logEvent('native_handoff_retried')
    hasLoggedBlockedRef.current = false
    setState('redirecting')
    // Refreshes the marker's timestamp too: repeated retries across a long-enough
    // window must not let the marker's age (still counting from the very first
    // attempt) expire mid-retry-loop and drop back to an undetected blind redirect on
    // the next mount.
    markNativeHandoffAttempt(urlRef.current)
    // Pushed (not replaced) from a button tap so the hop carries user activation and
    // the user can come back to this screen if it's blocked again.
    redirectToUrl(urlRef.current, 'push')
    armGracePeriod()
  }, [enabled, armGracePeriod])

  const reset = useCallback((): void => {
    if (!enabled) return
    clearTimer()
    clearNativeHandoffAttempt()
    urlRef.current = null
    timeoutElapsedRef.current = false
    hasLoggedBlockedRef.current = false
    logEvent('native_handoff_reset')
    setState('idle')
  }, [enabled, clearTimer])

  return { state, start, retry, reset }
}
